import { isDefined } from "@clipboard-health/util-ts";
import { type CbhAppInfo, isCapacitorPlatform } from "@src/appV2/lib/utils/capacitor";
import semver from "semver";

export const MATCH_ALL_VERSIONS = "*";

interface IsEnabledForAppVersionAndOtaParams {
  minimalStoreVersion: string;
  minimalOtaVersion: string | number;
  appInfo?: CbhAppInfo;
}

/**
 * Checks if the app version and OTA version meet the minimum requirements.
 * - On web (non-Capacitor), always returns true
 * - On Capacitor, returns false if appInfo is not available
 * - Otherwise, compares versions against the minimums
 */
export function isEnabledForAppVersionAndOta(
  parameters: IsEnabledForAppVersionAndOtaParams
): boolean {
  const { minimalStoreVersion, minimalOtaVersion, appInfo } = parameters;

  if (!isCapacitorPlatform()) {
    return true;
  }

  if (
    !isDefined(appInfo) ||
    !isDefined(appInfo.version) ||
    !isDefined(minimalOtaVersion) ||
    !isDefined(minimalStoreVersion)
  ) {
    return false;
  }

  if (
    minimalStoreVersion !== MATCH_ALL_VERSIONS &&
    semver.gt(minimalStoreVersion, appInfo.version)
  ) {
    return false;
  }

  if (
    minimalOtaVersion !== MATCH_ALL_VERSIONS &&
    (minimalStoreVersion === MATCH_ALL_VERSIONS || semver.eq(minimalStoreVersion, appInfo.version))
  ) {
    const minimalOtaVersionAsNumber = Number(minimalOtaVersion);
    const appOtaAsNumber = Number(appInfo.otaBuildId);

    if (!Number.isFinite(minimalOtaVersionAsNumber) || !Number.isFinite(appOtaAsNumber)) {
      return false;
    }

    if (minimalOtaVersionAsNumber > appOtaAsNumber) {
      return false;
    }
  }

  return true;
}
